import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  Search,
  Phone,
  Mail,
  Globe,
  MapPin,
  Filter,
  Pencil,
  Archive,
  Users,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

import { API_BASE_URL as API } from "../lib/backendUrl";

const COMPANY_TYPES = [
  { value: "client", label: "Client", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { value: "vendor", label: "Vendor", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
  { value: "subcontractor", label: "Subcontractor", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  { value: "agency", label: "Agency", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  { value: "partner", label: "Partner", color: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30" },
];

const emptyForm = {
  name: "",
  legal_name: "",
  company_type: "client",
  website: "",
  phone: "",
  email: "",
  address_line1: "",
  city: "",
  state: "",
  postal_code: "",
  industry: "",
  employee_count: "",
  annual_revenue: "",
  certifications: [],
  notes: "",
};

const CompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingCompany, setEditingCompany] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });

  useEffect(() => {
    fetchCompanies();
  }, [filterType]);

  const fetchCompanies = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType && filterType !== "all") params.append("company_type", filterType);
      const res = await axios.get(`${API}/companies?${params}`);
      setCompanies(res.data);
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name) {
      toast.error("Company name is required");
      return;
    }
    try {
      await axios.post(`${API}/companies`, form);
      toast.success("Company created");
      setIsCreateOpen(false);
      setForm(emptyForm);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create company");
    }
  };

  const handleUpdate = async () => {
    if (!editingCompany) return;
    try {
      await axios.put(`${API}/companies/${editingCompany.id}`, form);
      toast.success("Company updated");
      setIsEditOpen(false);
      setEditingCompany(null);
      setForm(emptyForm);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update company");
    }
  };

  const handleArchive = async (company) => {
    try {
      await axios.delete(`${API}/companies/${company.id}`);
      toast.success("Company archived");
      fetchCompanies();
    } catch (error) {
      toast.error("Failed to archive company");
    }
  };

  const openEdit = (company) => {
    setEditingCompany(company);
    setForm({
      name: company.name || "",
      legal_name: company.legal_name || "",
      company_type: company.company_type || "client",
      website: company.website || "",
      phone: company.phone || "",
      email: company.email || "",
      address_line1: company.address_line1 || "",
      city: company.city || "",
      state: company.state || "",
      postal_code: company.postal_code || "",
      industry: company.industry || "",
      employee_count: company.employee_count || "",
      annual_revenue: company.annual_revenue || "",
      certifications: company.certifications || [],
      notes: company.notes || "",
    });
    setIsEditOpen(true);
  };

  const getTypeColor = (type) => {
    return COMPANY_TYPES.find(t => t.value === type)?.color || "bg-muted text-muted-foreground";
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const sortedCompanies = useMemo(() => {
    const filtered = companies.filter(c =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.industry?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key] || "";
      const bVal = b[sortConfig.key] || "";
      const comparison = aVal.toString().localeCompare(bVal.toString());
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [companies, searchQuery, sortConfig]);

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />;
    return sortConfig.direction === "asc" ? 
      <ArrowUp className="h-4 w-4 text-primary" /> : 
      <ArrowDown className="h-4 w-4 text-primary" />;
  };

  const CompanyForm = ({ onSubmit, submitLabel }) => (
    <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Company Name *</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Acme Construction"
            className="bg-muted/50"
          />
        </div>
        <div className="space-y-2">
          <Label>Legal Name</Label>
          <Input
            value={form.legal_name}
            onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
            placeholder="Acme Construction LLC"
            className="bg-muted/50"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Type</Label>
          <Select value={form.company_type} onValueChange={(v) => setForm({ ...form, company_type: v })}>
            <SelectTrigger className="bg-muted/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMPANY_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Industry</Label>
          <Input
            value={form.industry}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
            placeholder="Real Estate Development"
            className="bg-muted/50"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Phone</Label>
          <Input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="(555) 123-4567"
            className="bg-muted/50"
          />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="info@company.com"
            className="bg-muted/50"
          />
        </div>
        <div className="space-y-2">
          <Label>Website</Label>
          <Input
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            placeholder="https://company.com"
            className="bg-muted/50"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Address</Label>
        <Input
          value={form.address_line1}
          onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
          placeholder="123 Main Street"
          className="bg-muted/50"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>City</Label>
          <Input
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="bg-muted/50"
          />
        </div>
        <div className="space-y-2">
          <Label>State</Label>
          <Input
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
            className="bg-muted/50"
          />
        </div>
        <div className="space-y-2">
          <Label>Postal Code</Label>
          <Input
            value={form.postal_code}
            onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
            className="bg-muted/50"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Additional notes..."
          className="bg-muted/50"
          rows={3}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={() => { setIsCreateOpen(false); setIsEditOpen(false); }}>
          Cancel
        </Button>
        <Button onClick={onSubmit} className="bg-primary">
          {submitLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading">Companies</h1>
          <p className="text-muted-foreground">Manage clients, vendors, and subcontractors</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary" onClick={() => setForm(emptyForm)} data-testid="add-company-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add Company</DialogTitle>
            </DialogHeader>
            <CompanyForm onSubmit={handleCreate} submitLabel="Create Company" />
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-muted/50"
            data-testid="company-search"
          />
        </div>
        <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 bg-muted/50" data-testid="company-type-filter">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {COMPANY_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats Summary */}
      <div className="flex gap-2 flex-wrap">
        {COMPANY_TYPES.map(type => {
          const count = companies.filter(c => c.company_type === type.value).length;
          return (
            <Badge
              key={type.value}
              variant="outline"
              className={`cursor-pointer transition-all ${filterType === type.value ? type.color : "hover:bg-muted"}`}
              onClick={() => setFilterType(filterType === type.value ? "" : type.value)}
            >
              {type.label}: {count}
            </Badge>
          );
        })}
      </div>

      {/* Company Table */}
      <div className="rounded-lg border border-border/50 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead 
                className="cursor-pointer select-none" 
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center gap-2">
                  Name <SortIcon column="name" />
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer select-none"
                onClick={() => handleSort("company_type")}
              >
                <div className="flex items-center gap-2">
                  Type <SortIcon column="company_type" />
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer select-none"
                onClick={() => handleSort("industry")}
              >
                <div className="flex items-center gap-2">
                  Industry <SortIcon column="industry" />
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer select-none"
                onClick={() => handleSort("city")}
              >
                <div className="flex items-center gap-2">
                  Location <SortIcon column="city" />
                </div>
              </TableHead>
              <TableHead>Contact</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                  Loading companies...
                </TableCell>
              </TableRow>
            ) : sortedCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No companies found</p>
                  <Button className="mt-4" onClick={() => setIsCreateOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add First Company
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              sortedCompanies.map((company) => (
                <TableRow key={company.id} className="hover:bg-muted/20" data-testid={`company-row-${company.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-medium">{company.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getTypeColor(company.company_type)}>
                      {company.company_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {company.industry || "-"}
                  </TableCell>
                  <TableCell>
                    {company.city && company.state ? (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {company.city}, {company.state}
                      </span>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3 text-sm">
                      {company.phone && (
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          {company.phone}
                        </span>
                      )}
                      {company.email && (
                        <a href={`mailto:${company.email}`} className="flex items-center gap-1 text-primary hover:underline">
                          <Mail className="h-3 w-3" />
                          Email
                        </a>
                      )}
                      {company.website && (
                        <a href={company.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                          <Globe className="h-3 w-3" />
                          Web
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEdit(company)}
                        data-testid={`edit-company-${company.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleArchive(company)}
                        data-testid={`archive-company-${company.id}`}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="text-sm text-muted-foreground">
        Showing {sortedCompanies.length} of {companies.length} companies
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-card max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
          </DialogHeader>
          <CompanyForm onSubmit={handleUpdate} submitLabel="Save Changes" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompaniesPage;
